import { useState, type ReactNode } from "react";
import { useAuth } from "~/lib/auth";
import { useMfaState } from "~/lib/totp";
import { useTheme } from "~/styles/theme";
import { Button, Card, Input, Screen, Text } from "./ui";

function MfaChallenge() {
  const { colors } = useTheme();
  const { signOut, completeMfaSignIn } = useAuth();
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await (recovery
        ? completeMfaSignIn({ recoveryCode: code })
        : completeMfaSignIn({ code }));
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "That code did not work.",
      );
      setBusy(false);
    }
  };

  return (
    <Screen scroll>
      <Card style={{ gap: 12, marginTop: 48 }}>
        <Text variant="heading">Two-factor authentication</Text>
        <Text variant="body" color={colors.muted}>
          {recovery
            ? "Enter one of your recovery codes to finish signing in."
            : "Enter the 6-digit code from your authenticator app to finish signing in."}
        </Text>
        <Input
          label={recovery ? "Recovery code" : "6-digit code"}
          value={code}
          onChangeText={setCode}
          keyboardType={recovery ? "default" : "number-pad"}
          autoCapitalize={recovery ? "characters" : "none"}
        />
        {error ? (
          <Text variant="caption" color={colors.danger}>
            {error}
          </Text>
        ) : null}
        <Button
          label="Verify"
          disabled={code.trim().length < (recovery ? 10 : 6)}
          loading={busy}
          onPress={() => void submit()}
          fullWidth
        />
        <Button
          label={recovery ? "Use authenticator code" : "Use a recovery code"}
          variant="ghost"
          onPress={() => {
            setRecovery((value) => !value);
            setCode("");
            setError("");
          }}
          fullWidth
        />
        <Button
          label="Sign out"
          variant="ghost"
          onPress={() => void signOut()}
          fullWidth
        />
      </Card>
    </Screen>
  );
}

/**
 * Holds the app back until a signed-in person who turned on two-factor
 * authentication has entered their code. Everyone else passes straight through.
 */
export function MfaGate({ children }: { children: ReactNode }) {
  const state = useMfaState();
  if (state === "checking") return null;
  if (state === "required") return <MfaChallenge />;
  return <>{children}</>;
}
